import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { PageHero, Section } from "@/components/site/section";
import { pollsQuery } from "@/lib/site-queries";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/forum/polls")({
  head: () => ({
    meta: [
      { title: "Polls | Argentinian.Soccer" },
      { name: "description", content: "Vote in Argentinian.Soccer community polls about coach, GOAT, goalkeeper and more." },
      { property: "og:title", content: "Polls | Argentinian.Soccer" },
      { property: "og:url", content: "/forum/polls" },
    ],
    links: [{ rel: "canonical", href: "/forum/polls" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(pollsQuery()),
  component: PollsPage,
});

function PollsPage() {
  const { data: polls } = useSuspenseQuery(pollsQuery());
  const qc = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [voting, setVoting] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setUserId(s?.user?.id ?? null));
    return () => sub.subscription.unsubscribe();
  }, []);

  async function vote(pollId: string, optionId: string) {
    if (!userId) {
      toast.error("Sign in to vote.");
      return;
    }
    setVoting(pollId);
    const { error } = await supabase.from("poll_votes").insert({
      poll_id: pollId,
      option_id: optionId,
      user_id: userId,
    });
    setVoting(null);
    if (error) {
      toast.error(error.message.includes("duplicate") ? "You already voted in this poll." : error.message);
      return;
    }
    toast.success("Vote recorded.");
    qc.invalidateQueries({ queryKey: ["polls"] });
  }

  return (
    <>
      <PageHero eyebrow="Community" title="Polls" />
      <Section>
        {!userId && (
          <div className="mb-6 rounded-md border border-border bg-secondary/60 p-4 text-sm">
            <Link to="/log-in" className="font-semibold text-primary">Sign in</Link> to vote — one vote per poll.
          </div>
        )}
        <div className="space-y-8">
          {polls.map((p) => (
            <div key={p.id} className="rounded-lg border border-border bg-card p-6">
              <h2 className="font-display text-xl font-bold text-navy">{p.question}</h2>
              <div className="mt-4 space-y-3">
                {p.options.map((opt) => {
                  const pct = p.total ? Math.round((opt.votes / p.total) * 100) : 0;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => vote(p.id, opt.id)}
                      disabled={voting === p.id}
                      className="block w-full rounded-md border border-border p-3 text-left hover:border-primary disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{opt.label}</span>
                        <span className="text-xs font-semibold text-muted-foreground">{opt.votes} · {pct}%</span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">{p.total} total votes</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}
